import { useMemo } from "react";
import { useQuery, useQueryClient, useMutation, type UseQueryResult } from "@tanstack/react-query";
import { isSquadCountAllowed, SQUAD_LIMIT_ERROR_CODE } from "@pulse/shared";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { Json, Tables } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { logEngagementEvent } from "@/lib/engagementEvents";
import { resolveDisplayName, fetchProfileMap } from "@/lib/profileDisplay";
import { useAuth } from "@/lib/auth";
import { fetchEventsByIds, fetchVenuesByIds, type PulseEvent } from "@/lib/events";
import { dismissDissolvedSquad, recordDissolvedSquad } from "@/lib/dissolvedSquads";
import { dismissLeftSquad, recordLeftSquad } from "@/lib/leftSquads";
import { pushNotification, purgeSquadNotifications } from "@/lib/squadNotifications";
import {
  clearPendingSelfLeave,
  markPendingSelfLeave,
} from "@/lib/squadLeaveSync";
import type { QueryClient } from "@tanstack/react-query";
import type { Friend } from "@/lib/friends";
import type { TimelineStop } from "@/lib/nightRoadmap";
import { fetchConsumerEntitlementsRpc } from "@/lib/consumerEntitlements";
import { parseSquadLimitFromPostgrest, SquadLimitError } from "@/lib/entitlementErrors";
import { MEMORY_PHOTO_PATH_RE, memoriesTable as memories } from "@/lib/squadMemories";
export {
  saveSquadNightToMemories,
  type Memory,
  saveMemory,
  fetchMemories,
  addPhotoToMemory,
  removePhotoFromMemory,
} from "@/lib/squadMemories";

const INVITES_KEY = "pulse.squadInvites";

export type SquadStatus = "planning" | "confirmed" | "live" | "done";
export type MemberStatus = "pending" | "accepted" | "declined";
export type VoteValue = "up" | "down";

export type Squad = {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  city: string;
  avatar_url: string | null;
  join_code: string;
  music_styles: string[];
  favorite_venue_ids: string[];
  settings_voting: boolean;
  settings_comments: boolean;
  settings_chat: boolean;
  status: "active" | "dissolved";
  created_at: string;
};

export type SquadMember = {
  id: string;
  squad_id: string;
  user_id: string;
  role: "admin" | "member";
  status: "active" | "invited";
  joined_at: string;
};

export interface SquadNight {
  id: string;
  name: string;
  date: string;
  hostId: string;
  city: string;
  budgetPerPerson: number | null;
  meetupLocation: string | null;
  meetupTime: string | null;
  status: SquadStatus;
  createdAt: string;
  squadId: string | null;
  mynightSnapshot: TimelineStop[] | null;
  savedToMemories: boolean;
}

/** Members shown on a squad night detail screen (persistent + mock + local invites). */
export interface SquadNightMember {
  id: string;
  squadId: string;
  userId: string | null;
  status: MemberStatus | SquadMember["status"];
  joinedAt: string;
  displayName?: string;
  avatar?: string;
  role?: "admin" | "member";
}

export interface SquadProposal {
  id: string;
  squadId: string;
  eventId: string | null;
  venueId: string | null;
  kind: "event" | "venue";
  freeText: string | null;
  venueName?: string | null;
  proposedBy: string | null;
  createdAt: string;
  timeFrom: string | null;
  timeTo: string | null;
  sortOrder: number;
  event?: PulseEvent | null;
}

export type ProposalComment = {
  id: string;
  proposalId: string;
  userId: string;
  message: string;
  createdAt: string;
};

export type AttendanceStatus = "coming" | "late" | "out";

export type SquadAttendance = {
  id: string;
  squadNightId: string;
  userId: string;
  status: AttendanceStatus;
  updatedAt: string;
};

export interface SquadVote {
  id: string;
  proposalId: string;
  userId: string;
  vote: VoteValue;
}

export interface SquadMessage {
  id: string;
  squadId: string;
  userId: string;
  message: string;
  createdAt: string;
  senderName?: string;
}

export interface SquadInvite {
  squadId: string;
  friendId: string;
  friendName: string;
  status: MemberStatus;
}

type SquadRow = Tables<"squads">;
type SquadNightRow = Tables<"squad_nights">;
type SquadNightSelectRow = Pick<
  SquadNightRow,
  | "id"
  | "name"
  | "date"
  | "host_id"
  | "city"
  | "budget_per_person"
  | "meetup_location"
  | "meetup_time"
  | "status"
  | "created_at"
  | "squad_id"
  | "mynight_snapshot"
  | "saved_to_memories"
>;
type MemberRow = Tables<"squad_members">;
type ProposalRow = Tables<"squad_event_proposals">;
type VoteRow = Tables<"squad_votes">;
type VoteSelectRow = Pick<VoteRow, "id" | "proposal_id" | "user_id" | "vote">;
type MessageRow = Tables<"squad_messages">;
type CommentRow = Tables<"squad_proposal_comments">;
type AttendanceRow = Tables<"squad_attendance">;

export type MynightSnapshotStop = {
  kind: "event" | "venue";
  id: string;
  name: string;
  startsAt: string | null;
  venueId: string | null;
  eventId: string | null;
  category?: string | null;
};

const SQUAD_FIELDS =
  "id, name, description, created_by, city, avatar_url, join_code, music_styles, favorite_venue_ids, settings_voting, settings_comments, settings_chat, status, created_at" as const;
const SQUAD_NIGHT_FIELDS =
  "id, name, date, host_id, city, budget_per_person, meetup_location, meetup_time, status, created_at, squad_id, mynight_snapshot, saved_to_memories" as const;
const SQUAD_MEMBER_FIELDS = "id, squad_id, user_id, role, status, joined_at" as const;
const SQUAD_PROPOSAL_FIELDS =
  "id, squad_id, event_id, venue_id, kind, free_text, proposed_by, created_at, time_from, time_to, sort_order" as const;
const SQUAD_VOTE_FIELDS = "id, proposal_id, user_id, vote" as const;
const SQUAD_MESSAGE_FIELDS = "id, squad_id, user_id, message, created_at" as const;
const PROPOSAL_COMMENT_FIELDS = "id, proposal_id, user_id, message, created_at" as const;
const SQUAD_ATTENDANCE_FIELDS = "id, squad_night_id, user_id, status, updated_at" as const;

function mapPersistentSquad(row: SquadRow): Squad {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    created_by: row.created_by,
    city: row.city,
    avatar_url: row.avatar_url,
    join_code: row.join_code,
    music_styles: row.music_styles ?? [],
    favorite_venue_ids: row.favorite_venue_ids ?? [],
    settings_voting: row.settings_voting,
    settings_comments: row.settings_comments,
    settings_chat: row.settings_chat,
    status: row.status === "dissolved" ? "dissolved" : "active",
    created_at: row.created_at,
  };
}

function parseMynightSnapshot(raw: Json | null): TimelineStop[] | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw as unknown as TimelineStop[];
  return null;
}

function buildMynightSnapshotStops(stops?: TimelineStop[]): MynightSnapshotStop[] {
  return (
    stops?.map((s) => ({
      kind: s.kind,
      id: s.kind === "venue" ? s.venue.id : s.event.id,
      name: s.kind === "venue" ? s.venue.name : s.event.name,
      startsAt: s.kind === "event" ? s.event.startsAtIso : null,
      venueId: s.kind === "venue" ? s.venue.id : null,
      eventId: s.kind === "event" ? s.event.id : null,
      category: s.kind === "venue" ? s.venue.category : s.event.category,
    })) ?? []
  );
}

function mapSquadNight(row: SquadNightSelectRow): SquadNight {
  return {
    id: row.id,
    name: row.name,
    date: row.date ?? new Date().toISOString().slice(0, 10),
    hostId: row.host_id ?? "",
    city: row.city ?? "Zurich",
    budgetPerPerson: row.budget_per_person,
    meetupLocation: row.meetup_location,
    meetupTime: row.meetup_time,
    status: (row.status as SquadStatus) ?? "planning",
    createdAt: row.created_at ?? new Date().toISOString(),
    squadId: row.squad_id,
    mynightSnapshot: parseMynightSnapshot(row.mynight_snapshot),
    savedToMemories: row.saved_to_memories ?? false,
  };
}

function mapNightMember(
  row: MemberRow,
  displayName?: string,
  avatar?: string,
): SquadNightMember {
  return {
    id: row.id,
    squadId: row.squad_id,
    userId: row.user_id,
    status: row.status === "invited" ? "invited" : row.status === "active" ? "active" : "accepted",
    joinedAt: row.joined_at,
    displayName,
    avatar,
    role: (row.role as "admin" | "member") ?? "member",
  };
}

async function fetchMembersForSquad(squadId: string): Promise<SquadNightMember[]> {
  const { data: membersData, error: membersError } = await supabase
    .from("squad_members")
    .select(SQUAD_MEMBER_FIELDS)
    .eq("squad_id", squadId);

  if (membersError) throw membersError;

  const userIds = (membersData ?? []).map((m) => m.user_id).filter(Boolean) as string[];

  const profileMap = userIds.length > 0 ? await fetchProfileMap(userIds) : new Map();

  return (membersData ?? []).map((row) => {
    const profile = profileMap.get(row.user_id ?? "");
    const displayName = profile ? resolveDisplayName(profile) : undefined;
    return mapNightMember(row, displayName, profile?.avatar_url ?? undefined);
  });
}

function mapProposal(
  row: ProposalRow,
  event?: PulseEvent | null,
  resolvedVenueName?: string | null,
): SquadProposal {
  const kind = row.kind === "venue" ? "venue" : "event";
  return {
    id: row.id,
    squadId: row.squad_id ?? "",
    eventId: row.event_id,
    venueId: row.venue_id,
    kind,
    freeText: row.free_text,
    venueName: kind === "venue" ? (resolvedVenueName ?? row.free_text) : null,
    proposedBy: row.proposed_by,
    createdAt: row.created_at ?? new Date().toISOString(),
    timeFrom: row.time_from ?? null,
    timeTo: row.time_to ?? null,
    sortOrder: row.sort_order ?? 0,
    event: event ?? null,
  };
}

function mapProposalComment(row: CommentRow): ProposalComment {
  return {
    id: row.id,
    proposalId: row.proposal_id,
    userId: row.user_id,
    message: row.message,
    createdAt: row.created_at,
  };
}

function mapSquadAttendance(row: AttendanceRow): SquadAttendance {
  return {
    id: row.id,
    squadNightId: row.squad_night_id,
    userId: row.user_id,
    status: row.status as AttendanceStatus,
    updatedAt: row.updated_at,
  };
}

function mapVote(row: VoteSelectRow): SquadVote {
  return { id: row.id, proposalId: row.proposal_id ?? "", userId: row.user_id ?? "", vote: row.vote as VoteValue };
}

function mapMessage(row: MessageRow, senderName?: string): SquadMessage {
  return {
    id: row.id,
    squadId: row.squad_id ?? "",
    userId: row.user_id ?? "",
    message: row.message,
    createdAt: row.created_at ?? new Date().toISOString(),
    senderName,
  };
}

function readInvites(): SquadInvite[] {
  try {
    const raw = localStorage.getItem(INVITES_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function writeInvites(invites: SquadInvite[]) {
  try {
    localStorage.setItem(INVITES_KEY, JSON.stringify(invites));
  } catch {}
}

function purgeSquadInvites(squadId: string, nightIds: string[] = []) {
  const ids = new Set([squadId, ...nightIds]);
  writeInvites(readInvites().filter((invite) => !ids.has(invite.squadId)));
}

function dissolveFunctionUrl(): string {
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) throw new Error("Missing VITE_SUPABASE_URL");
  return `${base}/functions/v1/dissolve-squad`;
}

function dissolveAnonKey(): string {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) throw new Error("Missing VITE_SUPABASE_ANON_KEY");
  return key;
}

async function dissolveAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Not authenticated");
  return {
    Authorization: `Bearer ${session.access_token}`,
    apikey: dissolveAnonKey(),
  };
}

type DissolveEdgeCounts = {
  memberCount: number;
  nightCount: number;
  proposalCount: number;
  voteCount: number;
  commentCount: number;
  messageCount: number;
  attendanceCount: number;
  memoryCount: number;
  photoFileCount: number;
};

type DissolveEdgePreviewResponse = DissolveEdgeCounts & {
  ok: boolean;
  preview?: boolean;
  error?: string;
  squadId?: string;
  squadName?: string;
};

type DissolveEdgeDeleteResponse = {
  ok: boolean;
  error?: string;
  deleted?: DissolveEdgeCounts & {
    squadId: string;
    squadName: string;
    storageRemoved?: number;
    storageFailed?: number;
  };
};

export type SquadDissolvePreview = DissolveEdgeCounts & {
  squadId: string;
  squadName: string;
  nightIds: string[];
  proposalIds: string[];
};

export type DissolveProgressStep = "loading" | "removing-photos" | "deleting" | "done";

export type SquadDissolvedPayload = {
  squadId: string;
  nightIds: string[];
  proposalIds: string[];
  userId: string;
  squadName?: string;
};

export type SquadLeavePreview = {
  squadId: string;
  squadName: string;
  ownMemoryCount: number;
  photoFileCount: number;
  nightIds: string[];
  proposalIds: string[];
};

export type SquadLeftPayload = {
  squadId: string;
  userId: string;
  squadName?: string;
  joinCode?: string;
  reason?: SquadRemovalReason;
  nightIds?: string[];
  proposalIds?: string[];
};

export type LeaveSquadResult =
  | { blocked: true; reason: "last-admin" }
  | { blocked: false; memoryCount: number; photoCount: number };


const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function loadDissolveNightIds(squadId: string): Promise<string[]> {
  const { data, error } = await supabase.from("squad_nights").select("id").eq("squad_id", squadId);
  if (error) throw error;
  return (data ?? []).map((row) => row.id);
}

async function loadDissolveProposalIds(nightIds: string[]): Promise<string[]> {
  if (nightIds.length === 0) return [];
  const { data, error } = await supabase
    .from("squad_event_proposals")
    .select("id")
    .in("squad_id", nightIds);
  if (error) throw error;
  return (data ?? []).map((row) => row.id);
}

async function callDissolveEdgeFunction(
  squadId: string,
  preview: boolean,
): Promise<DissolveEdgePreviewResponse | DissolveEdgeDeleteResponse> {
  const headers = await dissolveAuthHeaders();

  if (preview) {
    const url = new URL(dissolveFunctionUrl());
    url.searchParams.set("preview", "true");
    url.searchParams.set("squadId", squadId);
    const res = await fetch(url.toString(), { headers });
    const body = (await res.json()) as DissolveEdgePreviewResponse;
    if (!res.ok || !body.ok) {
      throw new Error(body.error ?? `Preview failed (${res.status})`);
    }
    return body;
  }

  const res = await fetch(dissolveFunctionUrl(), {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ squadId }),
  });
  const body = (await res.json()) as DissolveEdgeDeleteResponse;
  if (!res.ok || !body.ok) {
    throw new Error(body.error ?? `Dissolve failed (${res.status})`);
  }
  return body;
}

export async function fetchSquadDissolvePreview(squadId: string): Promise<SquadDissolvePreview> {
  const [edgeRes, nightIds] = await Promise.all([
    callDissolveEdgeFunction(squadId, true) as Promise<DissolveEdgePreviewResponse>,
    loadDissolveNightIds(squadId),
  ]);

  const proposalIds = await loadDissolveProposalIds(nightIds);

  return {
    squadId: edgeRes.squadId ?? squadId,
    squadName: edgeRes.squadName ?? "Squad",
    nightIds,
    proposalIds,
    memberCount: edgeRes.memberCount ?? 0,
    nightCount: edgeRes.nightCount ?? nightIds.length,
    proposalCount: edgeRes.proposalCount ?? 0,
    voteCount: edgeRes.voteCount ?? 0,
    commentCount: edgeRes.commentCount ?? 0,
    messageCount: edgeRes.messageCount ?? 0,
    attendanceCount: edgeRes.attendanceCount ?? 0,
    memoryCount: edgeRes.memoryCount ?? 0,
    photoFileCount: edgeRes.photoFileCount ?? 0,
  };
}

export async function dissolveSquad(
  squadId: string,
  onProgress?: (step: DissolveProgressStep) => void,
): Promise<NonNullable<DissolveEdgeDeleteResponse["deleted"]>> {
  onProgress?.("loading");
  await sleep(150);
  onProgress?.("removing-photos");

  const [edgeRes] = await Promise.all([
    callDissolveEdgeFunction(squadId, false) as Promise<DissolveEdgeDeleteResponse>,
    sleep(400),
  ]);

  onProgress?.("deleting");
  await sleep(250);

  const deleted = edgeRes.deleted;
  if (!deleted) {
    throw new Error("Squad could not be dissolved.");
  }

  onProgress?.("done");
  return deleted;
}

/** Resolve squad display name from React Query cache before eviction. */
export function resolveDissolvedSquadName(
  queryClient: QueryClient,
  squadId: string,
  userId: string,
  fallback?: string,
): string {
  const fromUserSquads = queryClient
    .getQueryData<Squad[]>(userSquadsQueryKey(userId))
    ?.find((s) => s.id === squadId)?.name;
  if (fromUserSquads) return fromUserSquads;

  const fromSettings = queryClient
    .getQueryData<Array<{ id: string; name: string }>>(settingsSquadsQueryKey(userId))
    ?.find((s) => s.id === squadId)?.name;
  if (fromSettings) return fromSettings;

  const ctx = queryClient.getQueryData<SquadPageContext>(squadPageContextKey(squadId));
  if (ctx?.persistentSquad?.name) return ctx.persistentSquad.name;

  return fallback ?? "Squad";
}

type SquadLifecycleCacheOptions = {
  squadId: string;
  userId: string;
  nightIds?: string[];
  proposalIds?: string[];
  /** Guarded setQueryData filter on user-squads + settings-squads (dissolve/leave). */
  evictFromLists?: boolean;
  /** Also invalidate ["memories", userId] (dissolve/leave). */
  invalidateMemories?: boolean;
  /** Await refetch on user-squads, settings-squads, squads (all three events). */
  refetchLists?: boolean;
  /** Await refetch on squad-page + squad-nights (join only today). */
  refetchSquadPage?: boolean;
  /** removeQueries for nights + proposals (dissolve/leave). */
  removeNightCaches?: boolean;
};

function evictSquadFromListCaches(
  queryClient: QueryClient,
  squadId: string,
  userId: string,
): void {
  // Only patch cache when a loaded snapshot actually contains this squad.
  // Never write [] when old is undefined — that permanently drops squads that
  // were never in this query's cache (e.g. Test1 visible in settings-squads only).
  queryClient.setQueryData<Squad[]>(userSquadsQueryKey(userId), (old) => {
    if (!old?.length || !old.some((s) => s.id === squadId)) return old;
    return old.filter((s) => s.id !== squadId);
  });
  queryClient.setQueryData<Array<{ id: string; name: string }>>(
    settingsSquadsQueryKey(userId),
    (old) => {
      if (!old?.length || !old.some((s) => s.id === squadId)) return old;
      return old.filter((s) => s.id !== squadId);
    },
  );
}

/** Shared React Query sync for squad membership lifecycle events. */
async function syncSquadLifecycleCache(
  queryClient: QueryClient,
  options: SquadLifecycleCacheOptions,
): Promise<void> {
  const {
    squadId,
    userId,
    nightIds = [],
    proposalIds = [],
    evictFromLists = false,
    invalidateMemories = false,
    refetchLists = false,
    refetchSquadPage = false,
    removeNightCaches = false,
  } = options;

  if (evictFromLists) {
    evictSquadFromListCaches(queryClient, squadId, userId);
  }

  const invalidations: Promise<void>[] = [
    queryClient.invalidateQueries({ queryKey: userSquadsQueryKey(userId) }),
    queryClient.invalidateQueries({ queryKey: squadsQueryKey(userId) }),
    queryClient.invalidateQueries({ queryKey: settingsSquadsQueryKey(userId) }),
    queryClient.invalidateQueries({ queryKey: squadPageContextKey(squadId) }),
    queryClient.invalidateQueries({ queryKey: squadNightsForSquadKey(squadId) }),
    queryClient.invalidateQueries({ queryKey: ["squad-admin", squadId] }),
    queryClient.invalidateQueries({ queryKey: ["squad-members-settings", squadId] }),
  ];
  if (invalidateMemories) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: ["memories", userId] }),
    );
  }
  await Promise.all(invalidations);

  const refetches: Promise<unknown>[] = [];
  if (refetchLists) {
    // TODO(lifecycle-cache): invalidate-only may suffice on RQ v5 for active
    // observers — kept as explicit refetch until all list UIs are verified.
    refetches.push(
      queryClient.refetchQueries({ queryKey: userSquadsQueryKey(userId), type: "all" }),
      queryClient.refetchQueries({ queryKey: settingsSquadsQueryKey(userId), type: "all" }),
      queryClient.refetchQueries({ queryKey: squadsQueryKey(userId), type: "all" }),
    );
  }
  if (refetchSquadPage) {
    refetches.push(
      queryClient.refetchQueries({ queryKey: squadPageContextKey(squadId), type: "all" }),
      queryClient.refetchQueries({ queryKey: squadNightsForSquadKey(squadId), type: "all" }),
    );
  }
  if (refetches.length > 0) {
    await Promise.all(refetches);
  }

  if (removeNightCaches) {
    for (const nightId of nightIds) {
      queryClient.removeQueries({ queryKey: squadDetailKey(nightId) });
      queryClient.removeQueries({ queryKey: squadAttendanceKey(nightId) });
      queryClient.removeQueries({ queryKey: squadPageContextKey(nightId) });
    }
    for (const proposalId of proposalIds) {
      queryClient.removeQueries({ queryKey: proposalCommentsKey(proposalId) });
    }
  }
}

export async function onSquadDissolved(
  queryClient: QueryClient,
  { squadId, nightIds, proposalIds, userId, squadName }: SquadDissolvedPayload,
): Promise<void> {
  const name = resolveDissolvedSquadName(queryClient, squadId, userId, squadName);
  recordDissolvedSquad({ id: squadId, name });

  purgeSquadInvites(squadId, nightIds);
  purgeSquadNotifications(squadId, nightIds);

  await syncSquadLifecycleCache(queryClient, {
    squadId,
    userId,
    nightIds,
    proposalIds,
    evictFromLists: true,
    invalidateMemories: true,
    refetchLists: true,
    removeNightCaches: true,
  });
}

/** Resolve squad join code from React Query cache before eviction. */
export async function resolveSquadJoinCode(
  queryClient: QueryClient,
  squadId: string,
): Promise<string | undefined> {
  const userSquadsEntries = queryClient.getQueriesData<Squad[]>({ queryKey: ["user-squads"] });
  for (const [, data] of userSquadsEntries) {
    const match = data?.find((s) => s.id === squadId);
    if (match?.join_code) return match.join_code;
  }

  const settingsEntries = queryClient.getQueriesData<Array<{ id: string; join_code?: string }>>({
    queryKey: ["settings-squads"],
  });
  for (const [, data] of settingsEntries) {
    const match = data?.find((s) => s.id === squadId);
    if (match?.join_code) return match.join_code;
  }

  const ctx = queryClient.getQueryData<SquadPageContext>(squadPageContextKey(squadId));
  if (ctx?.persistentSquad?.join_code) return ctx.persistentSquad.join_code;

  const squad = await fetchSquadById(squadId);
  return squad?.join_code;
}

export async function fetchSquadLeavePreview(
  squadId: string,
  userId: string,
): Promise<SquadLeavePreview> {
  const [squad, nightIds] = await Promise.all([
    fetchSquadById(squadId),
    loadDissolveNightIds(squadId),
  ]);
  const proposalIds = await loadDissolveProposalIds(nightIds);

  let ownMemoryCount = 0;
  let photoFileCount = 0;

  if (nightIds.length > 0) {
    const { data: memoryRows, error } = await memories()
      .select("photo_urls")
      .eq("created_by", userId)
      .in("squad_night_id", nightIds);

    if (error) throw error;

    const rows = (memoryRows ?? []) as Array<{ photo_urls: string[] | null }>;
    ownMemoryCount = rows.length;
    photoFileCount = rows.reduce(
      (sum, row) => sum + (Array.isArray(row.photo_urls) ? row.photo_urls.length : 0),
      0,
    );
  }

  return {
    squadId,
    squadName: squad?.name ?? "Squad",
    ownMemoryCount,
    photoFileCount,
    nightIds,
    proposalIds,
  };
}

function parseMemoryPhotoPaths(photoUrls: string[]): string[] {
  return photoUrls
    .map((url) => MEMORY_PHOTO_PATH_RE.exec(url)?.[1] ?? "")
    .filter(Boolean);
}

export async function deleteOwnMemoriesForSquad(
  squadId: string,
  userId: string,
): Promise<{ memoryCount: number; photoCount: number }> {
  const nightIds = await loadDissolveNightIds(squadId);
  if (nightIds.length === 0) {
    return { memoryCount: 0, photoCount: 0 };
  }

  const { data: memoryRows, error: fetchErr } = await memories()
    .select("id, photo_urls")
    .eq("created_by", userId)
    .in("squad_night_id", nightIds);

  if (fetchErr) throw fetchErr;

  const rows = (memoryRows ?? []) as Array<{ id: string; photo_urls: string[] | null }>;
  if (rows.length === 0) {
    return { memoryCount: 0, photoCount: 0 };
  }

  const paths = rows.flatMap((row) =>
    parseMemoryPhotoPaths(Array.isArray(row.photo_urls) ? row.photo_urls : []),
  );
  const uniquePaths = [...new Set(paths)];

  if (uniquePaths.length > 0) {
    const { error: storageErr } = await supabase.storage.from("memory-photos").remove(uniquePaths);
    if (storageErr) {
      console.warn("[deleteOwnMemoriesForSquad] storage remove failed:", storageErr.message, uniquePaths);
    }
  }

  const memoryIds = rows.map((row) => row.id);
  const { error: deleteErr } = await memories().delete().eq("created_by", userId).in("id", memoryIds);

  if (deleteErr) throw deleteErr;

  return { memoryCount: rows.length, photoCount: uniquePaths.length };
}

export async function onSquadLeft(
  queryClient: QueryClient,
  { squadId, nightIds = [], proposalIds = [], userId, squadName, joinCode, reason }: SquadLeftPayload,
): Promise<void> {
  const name = resolveDissolvedSquadName(queryClient, squadId, userId, squadName);
  const code = joinCode ?? (await resolveSquadJoinCode(queryClient, squadId));

  let removalReason = reason;
  if (!removalReason) {
    const latest = await getLatestSquadRemoval(squadId, userId);
    removalReason = latest?.reason;
  }

  recordLeftSquad({ id: squadId, name, joinCode: code, reason: removalReason });

  dismissDissolvedSquad(squadId);

  const resolvedNightIds =
    nightIds.length > 0
      ? nightIds
      : (queryClient.getQueryData<SquadNight[]>(squadNightsForSquadKey(squadId))?.map((n) => n.id) ??
        []);

  purgeSquadInvites(squadId, resolvedNightIds);
  purgeSquadNotifications(squadId, resolvedNightIds);

  await syncSquadLifecycleCache(queryClient, {
    squadId,
    userId,
    nightIds: resolvedNightIds,
    proposalIds,
    evictFromLists: true,
    invalidateMemories: true,
    refetchLists: true,
    removeNightCaches: true,
  });
}

export const squadsQueryKey = (userId: string) => ["squads", userId] as const;
export const userSquadsQueryKey = (userId: string) => ["user-squads", userId] as const;
export const settingsSquadsQueryKey = (userId: string) => ["settings-squads", userId] as const;
export const squadNightsForSquadKey = (squadId: string) => ["squad-nights", squadId] as const;
export const squadDetailKey = (squadNightId: string) => ["squad", squadNightId] as const;
export const squadPageContextKey = (urlId: string) => ["squad-page", urlId] as const;
export const proposalCommentsKey = (proposalId: string) => ["proposal-comments", proposalId] as const;
export const squadAttendanceKey = (squadNightId: string) => ["squad-attendance", squadNightId] as const;

export type SquadPageContext = {
  persistentSquad: Squad | null;
  members: SquadNightMember[];
  initialNightId: string | null;
  isLegacyNightOnly: boolean;
};

/** Persistent friend groups the user belongs to (member or creator). */
export async function fetchUserSquads(userId: string): Promise<Squad[]> {
  const { data: memberships, error: mErr } = await supabase
    .from("squad_members")
    .select("squad_id")
    .eq("user_id", userId)
    .in("status", ["active", "invited"]);

  if (mErr) throw mErr;

  const { data: created, error: cErr } = await supabase
    .from("squads")
    .select(SQUAD_FIELDS)
    .eq("created_by", userId)
    .eq("status", "active");

  if (cErr) throw cErr;

  const ids = new Set<string>([
    ...(memberships ?? []).map((m) => m.squad_id),
    ...(created ?? []).map((s) => s.id),
  ]);

  if (ids.size === 0) return [];

  const { data, error } = await supabase
    .from("squads")
    .select(SQUAD_FIELDS)
    .in("id", [...ids])
    .eq("status", "active");
  if (error) throw error;

  return (data ?? []).map(mapPersistentSquad).sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchActiveSquadCount(userId: string): Promise<number> {
  const squads = await fetchUserSquads(userId);
  return squads.length;
}

async function assertCanAddSquadMembership(userId: string): Promise<void> {
  const [resolved, count] = await Promise.all([
    fetchConsumerEntitlementsRpc(),
    fetchActiveSquadCount(userId),
  ]);

  if (!resolved.entitlements || !isSquadCountAllowed(count, resolved.entitlements)) {
    throw new SquadLimitError(resolved.entitlements?.maxSquads ?? 1);
  }
}

function mapSquadMembershipError(error: unknown): never {
  const parsed = parseSquadLimitFromPostgrest(error);
  if (parsed) throw parsed;
  throw error;
}

export async function fetchSquadByJoinCode(code: string): Promise<Squad | null> {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return null;

  const { data, error } = await supabase.rpc("lookup_squad_by_join_code", {
    code: normalized,
  });

  if (error) throw error;

  const row = (Array.isArray(data) ? data[0] : data) as SquadRow | null | undefined;
  return row ? mapPersistentSquad(row) : null;
}

export const squadAdminKey = (squadId: string, userId: string) =>
  ["squad-admin", squadId, userId] as const;

export async function createSquad(
  name: string,
  city: string,
  options?: {
    description?: string;
    music_styles?: string[];
    favorite_venue_ids?: string[];
    settings_voting?: boolean;
    settings_comments?: boolean;
    settings_chat?: boolean;
  },
): Promise<Squad> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  await assertCanAddSquadMembership(user.id);

  const { data, error } = await supabase
    .from("squads")
    .insert({
      name: name.trim(),
      city: city.trim().toLowerCase() || "zurich",
      created_by: user.id,
      description: options?.description ?? null,
      music_styles: options?.music_styles ?? [],
      favorite_venue_ids: options?.favorite_venue_ids ?? [],
      settings_voting: options?.settings_voting ?? true,
      settings_comments: options?.settings_comments ?? true,
      settings_chat: options?.settings_chat ?? true,
    })
    .select(SQUAD_FIELDS)
    .single();

  if (error) throw error;

  const { error: memberErr } = await supabase.from("squad_members").insert({
    squad_id: data.id,
    user_id: user.id,
    status: "active",
    role: "admin",
  });

  if (memberErr) {
    if (memberErr.message?.includes(SQUAD_LIMIT_ERROR_CODE)) {
      mapSquadMembershipError(memberErr);
    }
    throw memberErr;
  }

  return mapPersistentSquad(data);
}

export async function isUserSquadAdmin(squadId: string): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase
    .from("squad_members")
    .select("role")
    .eq("squad_id", squadId)
    .eq("user_id", user.id)
    .eq("status", "active")
    .maybeSingle();

  if (error) throw error;
  return data?.role === "admin";
}

export async function updateSquadSettings(
  squadId: string,
  updates: Partial<
    Pick<
      Squad,
      | "name"
      | "description"
      | "music_styles"
      | "favorite_venue_ids"
      | "settings_voting"
      | "settings_comments"
      | "settings_chat"
    >
  >,
): Promise<void> {
  const { error } = await supabase.from("squads").update(updates).eq("id", squadId);
  if (error) throw error;
}

export async function updateMemberRole(
  squadId: string,
  userId: string,
  role: "admin" | "member",
): Promise<void> {
  const { error } = await supabase
    .from("squad_members")
    .update({ role })
    .eq("squad_id", squadId)
    .eq("user_id", userId);

  if (error) throw error;
}

export async function removeMember(squadId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from("squad_members")
    .delete()
    .eq("squad_id", squadId)
    .eq("user_id", userId);

  if (error) throw error;
}

export type SquadRemovalReason = "self_leave" | "kicked";

export type LatestSquadRemoval = {
  reason: SquadRemovalReason;
  removed_at: string;
  removed_by: string;
};

/** Atomic removal + audit log (Track B). Prefer over removeMember for leave/kick. */
export async function logAndRemoveMember(
  squadId: string,
  userId: string,
  reason: SquadRemovalReason,
): Promise<void> {
  const { error } = await supabase.rpc("log_and_remove_member", {
    p_squad_id: squadId,
    p_user_id: userId,
    p_reason: reason,
  });
  if (error) throw error;
}

export async function getLatestSquadRemoval(
  squadId: string,
  userId?: string,
): Promise<LatestSquadRemoval | null> {
  const { data, error } = await supabase.rpc("get_latest_removal", {
    p_squad_id: squadId,
    ...(userId !== undefined ? { p_user_id: userId } : {}),
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as LatestSquadRemoval | undefined;
  return row ?? null;
}

async function assertCanJoinSquadByCode(squadId: string, userId: string): Promise<void> {
  const { data: membership, error: memberErr } = await supabase
    .from("squad_members")
    .select("status")
    .eq("squad_id", squadId)
    .eq("user_id", userId)
    .maybeSingle();

  if (memberErr) throw memberErr;
  if (membership?.status === "active" || membership?.status === "invited") {
    return;
  }

  await assertCanAddSquadMembership(userId);

  const latest = await getLatestSquadRemoval(squadId, userId);
  if (!latest) return;

  if (latest.reason === "self_leave") {
    throw new Error(
      "Rejoin requires admin approval. Use Erneut beitreten to send a request.",
    );
  }
  if (latest.reason === "kicked") {
    throw new Error("You were removed from this squad. An admin must re-invite you.");
  }
}

export async function leaveSquad(squadId: string, userId: string): Promise<LeaveSquadResult> {
  const { data: membership, error: membershipErr } = await supabase
    .from("squad_members")
    .select("role")
    .eq("squad_id", squadId)
    .eq("user_id", userId)
    .eq("status", "active")
    .maybeSingle();

  if (membershipErr) throw membershipErr;
  if (!membership) {
    return { blocked: false, memoryCount: 0, photoCount: 0 };
  }

  if (membership.role === "admin") {
    const { count, error: countErr } = await supabase
      .from("squad_members")
      .select("id", { count: "exact", head: true })
      .eq("squad_id", squadId)
      .eq("role", "admin")
      .eq("status", "active");

    if (countErr) throw countErr;
    if ((count ?? 0) <= 1) {
      return { blocked: true, reason: "last-admin" };
    }
  }

  markPendingSelfLeave(squadId);
  try {
    const { memoryCount, photoCount } = await deleteOwnMemoriesForSquad(squadId, userId);
    await logAndRemoveMember(squadId, userId, "self_leave");
    // Keep flag long enough for Realtime squad_members DELETE to no-op in Phase A4.
    setTimeout(() => clearPendingSelfLeave(squadId), 3_000);
    return { blocked: false, memoryCount, photoCount };
  } catch (err) {
    clearPendingSelfLeave(squadId);
    throw err;
  }
}

export {
  clearPendingSelfLeave,
  isDissolvingSquad,
  isPendingSelfLeave,
  markDissolvingSquad,
  markPendingSelfLeave,
} from "@/lib/squadLeaveSync";

/** Refresh squad list caches after joining or rejoining by code. */
export async function onSquadJoined(
  queryClient: QueryClient,
  userId: string,
  squadId: string,
): Promise<void> {
  dismissLeftSquad(squadId);

  await syncSquadLifecycleCache(queryClient, {
    squadId,
    userId,
    refetchLists: true,
    refetchSquadPage: true,
  });
}

export async function joinSquadByCode(joinCode: string): Promise<Squad> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const squad = await fetchSquadByJoinCode(joinCode);
  if (!squad) throw new Error("Invalid join code");
  if (squad.status === "dissolved") throw new Error("Squad has been dissolved");

  await assertCanJoinSquadByCode(squad.id, user.id);

  const { error } = await supabase.from("squad_members").upsert(
    {
      squad_id: squad.id,
      user_id: user.id,
      status: "active",
      role: "member",
    },
    { onConflict: "squad_id,user_id" },
  );

  if (error) {
    if (error.message?.includes(SQUAD_LIMIT_ERROR_CODE)) {
      mapSquadMembershipError(error);
    }
    throw error;
  }
  return squad;
}

export async function regenerateJoinCode(squadId: string): Promise<string> {
  const joinCode = crypto.randomUUID().replace(/-/g, "").slice(0, 6).toUpperCase();
  const { data, error } = await supabase
    .from("squads")
    .update({ join_code: joinCode })
    .eq("id", squadId)
    .select("join_code")
    .single();

  if (error) throw error;
  return data.join_code;
}

/** Invite a user into a circle as `invited` (SECURITY DEFINER RPC). */
export async function inviteUserToSquad(squadId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc("reinvite_squad_member", {
    p_squad_id: squadId,
    p_user_id: userId,
  });
  if (error) throw error;
}

export async function fetchSquadById(squadId: string): Promise<Squad | null> {
  const { data, error } = await supabase
    .from("squads")
    .select(SQUAD_FIELDS)
    .eq("id", squadId)
    .maybeSingle();

  if (error) throw error;
  return data ? mapPersistentSquad(data) : null;
}

export async function fetchSquadGroupMembers(
  persistentSquadId: string,
  _fallbackNightId?: string,
): Promise<SquadNightMember[]> {
  return fetchMembersForSquad(persistentSquadId);
}

/** Resolve URL id as persistent squad id or legacy squad night id. */
export async function resolveSquadPageContext(urlId: string): Promise<SquadPageContext> {
  const persistentSquad = await fetchSquadById(urlId);
  if (persistentSquad) {
    const members = await fetchSquadGroupMembers(persistentSquad.id);
    return {
      persistentSquad,
      members,
      initialNightId: null,
      isLegacyNightOnly: false,
    };
  }

  const { data: nightRow, error: nightErr } = await supabase
    .from("squad_nights")
    .select(SQUAD_NIGHT_FIELDS)
    .eq("id", urlId)
    .maybeSingle();

  if (nightErr) throw nightErr;
  if (!nightRow) throw new Error("Squad not found");

  const night = mapSquadNight(nightRow);

  if (night.squadId) {
    const squad = await fetchSquadById(night.squadId);
    const members = await fetchSquadGroupMembers(night.squadId, urlId);
    return {
      persistentSquad: squad,
      members,
      initialNightId: urlId,
      isLegacyNightOnly: false,
    };
  }

  const detail = await fetchSquadDetail(urlId);
  return {
    persistentSquad: null,
    members: detail.members,
    initialNightId: urlId,
    isLegacyNightOnly: true,
  };
}

export async function fetchSquadNights(squadId: string): Promise<SquadNight[]> {
  const { data, error } = await supabase
    .from("squad_nights")
    .select(SQUAD_NIGHT_FIELDS)
    .eq("squad_id", squadId)
    .order("date", { ascending: true });

  if (error) throw error;
  return (data ?? []).map(mapSquadNight);
}

/** Squad nights for the profile list (hosted + nights of squads the user belongs to). */
export async function fetchUserSquadNights(userId: string): Promise<SquadNight[]> {
  const { data: hosted, error: hErr } = await supabase
    .from("squad_nights")
    .select(SQUAD_NIGHT_FIELDS)
    .eq("host_id", userId)
    .order("date", { ascending: true });

  if (hErr) throw hErr;

  const { data: memberships, error: mErr } = await supabase
    .from("squad_members")
    .select("squad_id")
    .eq("user_id", userId)
    .in("status", ["active", "invited"]);

  if (mErr) throw mErr;

  const persistentSquadIds = [...new Set((memberships ?? []).map((m) => m.squad_id))];
  let memberNights: SquadNightSelectRow[] = [];

  if (persistentSquadIds.length > 0) {
    const { data, error } = await supabase
      .from("squad_nights")
      .select(SQUAD_NIGHT_FIELDS)
      .in("squad_id", persistentSquadIds)
      .order("date", { ascending: true });
    if (error) throw error;
    memberNights = data ?? [];
  }

  const map = new Map<string, SquadNight>();
  for (const row of [...(hosted ?? []), ...memberNights]) {
    map.set(row.id, mapSquadNight(row));
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/** Squad night that is currently "active" for My Night auto-transform. */
export type ActiveSquadNight = {
  night: SquadNight;
  squad: Squad;
};

export const activeSquadNightsQueryKey = (userId: string) =>
  ["active-squad-nights", userId] as const;

const ACTIVE_NIGHT_DB_STATUSES: SquadStatus[] = ["planning", "confirmed"];

export function todayIsoDateLocal(): string {
  return new Date().toISOString().slice(0, 10);
}

export function compareActiveSquadNights(a: ActiveSquadNight, b: ActiveSquadNight): number {
  const today = todayIsoDateLocal();
  const aTonight = a.night.date === today ? 0 : 1;
  const bTonight = b.night.date === today ? 0 : 1;
  if (aTonight !== bTonight) return aTonight - bTonight;
  const dateCmp = a.night.date.localeCompare(b.night.date);
  if (dateCmp !== 0) return dateCmp;
  return b.night.createdAt.localeCompare(a.night.createdAt);
}

export function pickPrimaryActiveSquadNight(items: ActiveSquadNight[]): ActiveSquadNight | null {
  if (items.length === 0) return null;
  return [...items].sort(compareActiveSquadNights)[0]!;
}

/**
 * Batched: one squads fetch + one squad_nights query (no per-squad N+1).
 */
export async function fetchActiveSquadNightsForUser(userId: string): Promise<ActiveSquadNight[]> {
  const squads = await fetchUserSquads(userId);
  if (squads.length === 0) return [];

  const squadMap = new Map(squads.map((s) => [s.id, s]));
  const today = todayIsoDateLocal();

  const { data: nights, error } = await supabase
    .from("squad_nights")
    .select(SQUAD_NIGHT_FIELDS)
    .in("squad_id", [...squadMap.keys()])
    .gte("date", today)
    .in("status", ACTIVE_NIGHT_DB_STATUSES)
    .order("date", { ascending: true });

  if (error) throw error;

  const results: ActiveSquadNight[] = [];
  for (const row of nights ?? []) {
    const night = mapSquadNight(row);
    if (!night.squadId) continue;
    const squad = squadMap.get(night.squadId);
    if (!squad) continue;
    results.push({ night, squad });
  }

  return results.sort(compareActiveSquadNights);
}

export function useActiveSquadNights(): UseQueryResult<ActiveSquadNight[]> {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;

  return useQuery({
    queryKey: activeSquadNightsQueryKey(userId ?? "none"),
    queryFn: () => fetchActiveSquadNightsForUser(userId!),
    enabled: !!userId,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useActiveSquadNight(): {
  activeNights: ActiveSquadNight[];
  primary: ActiveSquadNight | null;
  isLoading: boolean;
  isError: boolean;
  refetch: UseQueryResult<ActiveSquadNight[]>["refetch"];
} {
  const query = useActiveSquadNights();
  const primary = useMemo(
    () => pickPrimaryActiveSquadNight(query.data ?? []),
    [query.data],
  );
  return {
    activeNights: query.data ?? [],
    primary,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export async function fetchSquadDetail(squadNightId: string): Promise<{
  squad: SquadNight;
  members: SquadNightMember[];
  proposals: SquadProposal[];
  votes: SquadVote[];
  messages: SquadMessage[];
}> {
  const { data: squadRow, error: sErr } = await supabase
    .from("squad_nights")
    .select(SQUAD_NIGHT_FIELDS)
    .eq("id", squadNightId)
    .single();

  if (sErr) throw sErr;

  const night = mapSquadNight(squadRow);
  const persistentSquadId = night.squadId;

  let members: SquadNightMember[] = [];

  if (persistentSquadId) {
    members = await fetchMembersForSquad(persistentSquadId);
  }

  const invites = readInvites().filter((i) => i.squadId === squadNightId);
  for (const inv of invites) {
    members.push({
      id: `inv-${inv.friendId}`,
      squadId: persistentSquadId ?? squadNightId,
      userId: null,
      status: inv.status,
      joinedAt: new Date().toISOString(),
      displayName: inv.friendName,
      role: "member",
    });
  }

  const [proposalsRes, messagesRes] = await Promise.all([
    supabase
      .from("squad_event_proposals")
      .select(SQUAD_PROPOSAL_FIELDS)
      .eq("squad_id", squadNightId)
      .order("sort_order")
      .order("created_at"),
    supabase
      .from("squad_messages")
      .select(SQUAD_MESSAGE_FIELDS)
      .eq("squad_id", squadNightId)
      .order("created_at"),
  ]);

  if (proposalsRes.error) throw proposalsRes.error;
  if (messagesRes.error) throw messagesRes.error;

  const proposalRows = proposalsRes.data ?? [];
  const eventIds = proposalRows.map((p) => p.event_id).filter((id): id is string => !!id);
  const venueIds = proposalRows.map((p) => p.venue_id).filter((id): id is string => !!id);
  const eventsMap = eventIds.length > 0 ? await fetchEventsByIds(eventIds) : new Map();
  const venuesMap = venueIds.length > 0 ? await fetchVenuesByIds(venueIds) : new Map();

  const proposals = proposalRows.map((p) =>
    mapProposal(
      p,
      p.event_id ? eventsMap.get(p.event_id) ?? null : null,
      p.venue_id ? venuesMap.get(p.venue_id)?.name ?? null : null,
    ),
  );

  const proposalIds = proposals.map((p) => p.id);
  let votes: SquadVote[] = [];
  if (proposalIds.length > 0) {
    const { data: voteRows, error: vErr } = await supabase
      .from("squad_votes")
      .select(SQUAD_VOTE_FIELDS)
      .in("proposal_id", proposalIds);
    if (vErr) throw vErr;
    votes = (voteRows ?? []).map(mapVote);
  }

  const messages = (messagesRes.data ?? []).map((m) => mapMessage(m));

  return { squad: night, members, proposals, votes, messages };
}

type CreateSquadNightInput = {
  name: string;
  date: string;
  city: string;
  hostId: string;
  squadId?: string;
  snapshot?: TimelineStop[];
  budgetPerPerson?: number | null;
  meetupLocation?: string | null;
  meetupTime?: string | null;
  inviteFriends?: Friend[];
};

async function insertSnapshotAsProposals(
  squadNightId: string,
  snapshot: MynightSnapshotStop[],
  proposedBy: string,
): Promise<void> {
  if (snapshot.length === 0) return;

  const proposals = snapshot.map((stop, index) => ({
    squad_id: squadNightId,
    kind: stop.kind,
    free_text: stop.name,
    venue_id: stop.venueId ?? null,
    event_id: stop.eventId ?? null,
    proposed_by: proposedBy,
    sort_order: index,
  }));

  const { error } = await supabase.from("squad_event_proposals").insert(proposals);
  if (error) throw error;

  for (const stop of snapshot) {
    if (stop.eventId) {
      logEngagementEvent({
        entityType: "event",
        entityId: stop.eventId,
        eventType: "proposal_added",
        userId: proposedBy,
        squadId: squadNightId,
      });
    } else if (stop.venueId) {
      logEngagementEvent({
        entityType: "venue",
        entityId: stop.venueId,
        eventType: "proposal_added",
        userId: proposedBy,
        squadId: squadNightId,
      });
    }
  }
}

async function createSquadNightFromInput(input: CreateSquadNightInput): Promise<SquadNight> {
  const snapshot = buildMynightSnapshotStops(input.snapshot);

  const { data, error } = await supabase
    .from("squad_nights")
    .insert({
      name: input.name.trim(),
      date: input.date,
      city: input.city,
      host_id: input.hostId,
      squad_id: input.squadId ?? null,
      mynight_snapshot: snapshot.length > 0 ? (snapshot as unknown as Json) : null,
      budget_per_person: input.budgetPerPerson ?? null,
      meetup_location: input.meetupLocation?.trim() || null,
      meetup_time: input.meetupTime?.trim() || null,
      status: "planning",
    })
    .select(SQUAD_NIGHT_FIELDS)
    .single();

  if (error) throw error;

  await insertSnapshotAsProposals(data.id, snapshot, input.hostId);

  if (input.inviteFriends?.length) {
    const squadId = input.squadId ?? data.squad_id;
    for (const f of input.inviteFriends) {
      const uid = "userId" in f && typeof f.userId === "string" ? f.userId : null;
      if (squadId && uid) {
        try {
          await inviteUserToSquad(squadId, uid);
        } catch {
          /* best-effort invite */
        }
      }
    }
  }

  return mapSquadNight(data);
}

/** Create a squad night linked to a persistent squad, with optional MyNight snapshot. */
export async function createSquadNight(
  squadId: string,
  name: string,
  date: string,
  snapshot?: TimelineStop[],
): Promise<SquadNight>;
export async function createSquadNight(input: CreateSquadNightInput): Promise<SquadNight>;
export async function createSquadNight(
  squadIdOrInput: string | CreateSquadNightInput,
  name?: string,
  date?: string,
  snapshot?: TimelineStop[],
): Promise<SquadNight> {
  if (typeof squadIdOrInput === "object") {
    return createSquadNightFromInput(squadIdOrInput);
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { data: squad, error: squadErr } = await supabase
    .from("squads")
    .select("city")
    .eq("id", squadIdOrInput)
    .maybeSingle();

  if (squadErr) throw squadErr;

  return createSquadNightFromInput({
    name: name!,
    date: date!,
    city: squad?.city ?? "zurich",
    hostId: user.id,
    squadId: squadIdOrInput,
    snapshot,
  });
}

export async function updateSquadNight(
  squadNightId: string,
  hostId: string,
  patch: Partial<
    Pick<
      SquadNight,
      "name" | "date" | "city" | "budgetPerPerson" | "meetupLocation" | "meetupTime" | "status" | "savedToMemories"
    >
  >,
): Promise<void> {
  const { error } = await supabase
    .from("squad_nights")
    .update({
      name: patch.name,
      date: patch.date,
      city: patch.city,
      budget_per_person: patch.budgetPerPerson,
      meetup_location: patch.meetupLocation,
      meetup_time: patch.meetupTime,
      status: patch.status,
      saved_to_memories: patch.savedToMemories,
    })
    .eq("id", squadNightId)
    .eq("host_id", hostId);

  if (error) throw error;
}

export async function deleteSquadNight(nightId: string): Promise<void> {
  const { error, data } = await supabase
    .from("squad_nights")
    .delete()
    .eq("id", nightId)
    .select("id");

  if (error) throw error;
  if (!data?.length) {
    throw new Error("Could not delete squad night — not found or permission denied");
  }
}

export async function fetchProposalComments(proposalId: string): Promise<ProposalComment[]> {
  const { data, error } = await supabase
    .from("squad_proposal_comments")
    .select(PROPOSAL_COMMENT_FIELDS)
    .eq("proposal_id", proposalId)
    .order("created_at");

  if (error) throw error;
  return (data ?? []).map(mapProposalComment);
}

export async function addProposalComment(proposalId: string, message: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const trimmed = message.trim();
  if (!trimmed) throw new Error("Message cannot be empty");

  const { error } = await supabase.from("squad_proposal_comments").insert({
    proposal_id: proposalId,
    user_id: user.id,
    message: trimmed,
  });

  if (error) throw error;
}

export async function fetchSquadAttendance(squadNightId: string): Promise<SquadAttendance[]> {
  const { data, error } = await supabase
    .from("squad_attendance")
    .select(SQUAD_ATTENDANCE_FIELDS)
    .eq("squad_night_id", squadNightId)
    .order("updated_at");

  if (error) throw error;
  return (data ?? []).map(mapSquadAttendance);
}

export async function setAttendance(
  squadNightId: string,
  status: AttendanceStatus,
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { error } = await supabase.from("squad_attendance").upsert(
    {
      squad_night_id: squadNightId,
      user_id: user.id,
      status,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "squad_night_id,user_id" },
  );

  if (error) throw error;
}

export async function updateProposalTime(
  proposalId: string,
  timeFrom: string | null,
  timeTo: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("squad_event_proposals")
    .update({ time_from: timeFrom, time_to: timeTo })
    .eq("id", proposalId);

  if (error) throw error;
}

export async function confirmSquadNight(nightId: string): Promise<void> {
  const { error } = await supabase
    .from("squad_nights")
    .update({ status: "confirmed" })
    .eq("id", nightId);
  if (error) throw error;
}

export async function updateProposalOrder(
  proposals: { id: string; sortOrder: number }[],
): Promise<void> {
  for (const { id, sortOrder } of proposals) {
    const { error } = await supabase
      .from("squad_event_proposals")
      .update({ sort_order: sortOrder })
      .eq("id", id);
    if (error) throw error;
  }
}

export async function proposeSquadEvent(
  squadNightId: string,
  userId: string,
  input: { eventId?: string; venueId?: string; freeText?: string; kind?: "event" | "venue" },
): Promise<SquadProposal> {
  const kind = input.kind ?? (input.venueId ? "venue" : "event");

  const { data, error } = await supabase
    .from("squad_event_proposals")
    .insert({
      squad_id: squadNightId,
      kind,
      event_id: input.eventId ?? null,
      venue_id: input.venueId ?? null,
      free_text: input.freeText?.trim() || null,
      proposed_by: userId,
    })
    .select(SQUAD_PROPOSAL_FIELDS)
    .single();

  if (error) throw error;

  if (data.event_id) {
    logEngagementEvent({
      entityType: "event",
      entityId: data.event_id,
      eventType: "proposal_added",
      userId,
      squadId: squadNightId,
    });
  } else if (data.venue_id) {
    logEngagementEvent({
      entityType: "venue",
      entityId: data.venue_id,
      eventType: "proposal_added",
      userId,
      squadId: squadNightId,
    });
  }

  pushNotification({
    type: "squad_proposal",
    title: "New proposal",
    body: input.freeText ?? "Someone proposed an event for your squad",
    squadId: squadNightId,
  });

  let event: PulseEvent | null = null;
  let venueName: string | null = null;
  if (data.event_id) {
    const map = await fetchEventsByIds([data.event_id]);
    event = map.get(data.event_id) ?? null;
  }
  if (data.venue_id) {
    const map = await fetchVenuesByIds([data.venue_id]);
    venueName = map.get(data.venue_id)?.name ?? null;
  }

  return mapProposal(data, event, venueName);
}

export async function deleteSquadProposal(proposalId: string, userId: string): Promise<void> {
  const { data: row, error: fetchError } = await supabase
    .from("squad_event_proposals")
    .select("event_id, venue_id, squad_id")
    .eq("id", proposalId)
    .maybeSingle();

  if (fetchError) throw fetchError;

  const { error } = await supabase
    .from("squad_event_proposals")
    .delete()
    .eq("id", proposalId);
  if (error) throw error;

  if (row?.event_id) {
    logEngagementEvent({
      entityType: "event",
      entityId: row.event_id,
      eventType: "proposal_removed",
      userId,
      squadId: row.squad_id,
    });
  } else if (row?.venue_id) {
    logEngagementEvent({
      entityType: "venue",
      entityId: row.venue_id,
      eventType: "proposal_removed",
      userId,
      squadId: row.squad_id,
    });
  }
}

export async function castSquadVote(
  proposalId: string,
  userId: string,
  vote: VoteValue,
): Promise<void> {
  const { data: existing } = await supabase
    .from("squad_votes")
    .select("id")
    .eq("proposal_id", proposalId)
    .eq("user_id", userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase.from("squad_votes").update({ vote }).eq("id", existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from("squad_votes").insert({
    proposal_id: proposalId,
    user_id: userId,
    vote,
  });

  if (error) throw error;
}

export async function sendSquadMessage(
  squadNightId: string,
  userId: string,
  message: string,
  senderName?: string,
): Promise<SquadMessage> {
  const trimmed = message.trim();
  if (!trimmed) throw new Error("Message cannot be empty");

  const { data, error } = await supabase
    .from("squad_messages")
    .insert({ squad_id: squadNightId, user_id: userId, message: trimmed })
    .select(SQUAD_MESSAGE_FIELDS)
    .single();

  if (error) throw error;

  pushNotification({
    type: "squad_message",
    title: "Squad chat",
    body: `${senderName ?? "Someone"}: ${trimmed.slice(0, 80)}`,
    squadId: squadNightId,
  });

  return mapMessage(data, senderName);
}

export function countVotes(proposalId: string, votes: SquadVote[]) {
  const mine = votes.filter((v) => v.proposalId === proposalId);
  return {
    up: mine.filter((v) => v.vote === "up").length,
    down: mine.filter((v) => v.vote === "down").length,
  };
}

export function isProposalConfirmed(
  proposalId: string,
  votes: SquadVote[],
  memberCount: number,
): boolean {
  const { up, down } = countVotes(proposalId, votes);
  const needed = Math.ceil(Math.max(memberCount, 1) / 2);
  return up >= needed && up > down;
}

export function getConfirmedProposals(
  proposals: SquadProposal[],
  votes: SquadVote[],
  memberCount: number,
): SquadProposal[] {
  return proposals.filter((p) => isProposalConfirmed(p.id, votes, memberCount));
}

export function pendingInviteCount(_userId: string, _squads: SquadNight[]): number {
  const invites = readInvites();
  return invites.filter((i) => i.status === "pending").length;
}

/**
 * Subscribe to new squad_messages for a night.
 * `purpose` must be unique per concurrent subscriber — Supabase reuses channels by
 * topic name and throws if `.on()` is called after `.subscribe()` on an existing one.
 * Prefer {@link acquireSharedRealtimeChannel} from `@/lib/sharedRealtimeChannel`
 * when multiple mounts need the same topic without a purpose suffix.
 */
export function subscribeSquadMessages(
  squadNightId: string,
  onMessage: (msg: SquadMessage) => void,
  purpose: string = "default",
): RealtimeChannel {
  return supabase
    .channel(`squad-messages-${squadNightId}-${purpose}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "squad_messages",
        filter: `squad_id=eq.${squadNightId}`,
      },
      (payload) => {
        onMessage(mapMessage(payload.new as MessageRow));
      },
    )
    .subscribe();
}

/** Lists squad nights for the profile Squad tab (legacy UI). */
export function useSquads() {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;

  return useQuery({
    queryKey: squadsQueryKey(userId ?? "none"),
    queryFn: () => fetchUserSquadNights(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useUserSquads(): UseQueryResult<Squad[]> {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;

  return useQuery({
    queryKey: userSquadsQueryKey(userId ?? "none"),
    queryFn: () => fetchUserSquads(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useSquadNights(squadId: string | undefined): UseQueryResult<SquadNight[]> {
  return useQuery({
    queryKey: squadNightsForSquadKey(squadId ?? "none"),
    queryFn: () => fetchSquadNights(squadId!),
    enabled: !!squadId,
    staleTime: 30_000,
  });
}

export function useSquadPageContext(urlId: string) {
  return useQuery({
    queryKey: squadPageContextKey(urlId),
    queryFn: () => resolveSquadPageContext(urlId),
    enabled: !!urlId,
    staleTime: 30_000,
  });
}

export function useSquadDetail(squadNightId: string) {
  return useQuery({
    queryKey: squadDetailKey(squadNightId),
    queryFn: () => fetchSquadDetail(squadNightId),
    enabled: !!squadNightId,
    staleTime: 10_000,
  });
}

export function useProposalComments(proposalId: string): UseQueryResult<ProposalComment[]> {
  return useQuery({
    queryKey: proposalCommentsKey(proposalId),
    queryFn: () => fetchProposalComments(proposalId),
    enabled: !!proposalId,
    staleTime: 10_000,
  });
}

export function useSquadAttendance(squadNightId: string): UseQueryResult<SquadAttendance[]> {
  return useQuery({
    queryKey: squadAttendanceKey(squadNightId),
    queryFn: () => fetchSquadAttendance(squadNightId),
    enabled: !!squadNightId,
    staleTime: 10_000,
  });
}

export function useIsSquadAdmin(squadId: string | null | undefined): boolean {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: squadAdminKey(squadId ?? "", user?.id ?? ""),
    queryFn: () => isUserSquadAdmin(squadId!),
    enabled: !!squadId && !!user,
    // Role can change remotely via realtime; keep short so invalidation feels immediate.
    staleTime: 5_000,
  });
  return data ?? false;
}

export function invalidateSquad(
  queryClient: ReturnType<typeof useQueryClient>,
  squadNightId?: string,
  userId?: string,
  persistentSquadId?: string,
  urlId?: string,
) {
  if (squadNightId) queryClient.invalidateQueries({ queryKey: squadDetailKey(squadNightId) });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: squadsQueryKey(userId) });
    queryClient.invalidateQueries({ queryKey: userSquadsQueryKey(userId) });
  }
  if (persistentSquadId) {
    queryClient.invalidateQueries({ queryKey: squadNightsForSquadKey(persistentSquadId) });
  }
  if (urlId) {
    queryClient.invalidateQueries({ queryKey: squadPageContextKey(urlId) });
  }
}

export function useCreateSquad() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: Omit<CreateSquadNightInput, "hostId">) =>
      createSquadNight({ ...input, hostId: user!.id! }),
    onSuccess: () => invalidateSquad(queryClient, undefined, user?.id),
  });
}

export function squadShareText(
  squad: SquadNight,
  confirmed: SquadProposal[],
  meetup?: { location?: string | null; time?: string | null },
): string {
  const lines = confirmed.map((p) => {
    if (p.event) return `🎉 ${p.event.name} @ ${p.event.venue}`;
    return `✨ ${p.freeText}`;
  });
  const header = `${squad.name} · ${squad.date} · ${squad.city}`;
  const meet = meetup?.location
    ? `\n📍 Meet: ${meetup.location}${meetup.time ? ` at ${meetup.time}` : ""}`
    : "";
  return `${header}${meet}\n${lines.join("\n")}`;
}

/** Mock 2h reminder for squads happening today */
export function mockSquadReminder(squad: SquadNight): string | null {
  const today = new Date().toISOString().slice(0, 10);
  if (squad.date !== today || !squad.meetupTime) return null;
  const [hRaw, mRaw] = squad.meetupTime.split(":").map(Number);
  if (hRaw === undefined || Number.isNaN(hRaw)) return null;
  const meet = new Date();
  meet.setHours(hRaw, mRaw ?? 0, 0, 0);
  const diff = meet.getTime() - Date.now();
  if (diff <= 0 || diff > 2 * 3_600_000) return null;
  return `${squad.name} starts in ${Math.max(1, Math.round(diff / 3_600_000))}h`;
}

export function notifySquadReminder(squad: SquadNight) {
  const msg = mockSquadReminder(squad);
  if (!msg) return;
  pushNotification({
    type: "squad_reminder",
    title: "Squad night soon",
    body: msg,
    squadId: squad.id,
  });
}

export function notifyProposalConfirmed(squadNightId: string, label: string) {
  pushNotification({
    type: "squad_confirmed",
    title: "Plan confirmed",
    body: `${label} confirmed by majority vote`,
    squadId: squadNightId,
  });
}
